import React, { useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Grid,
  Divider,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import { Close, Person } from '@material-ui/icons'
import { ApexOptions } from "apexcharts"
import ReactApexChart from "react-apexcharts"
import { StatCard } from 'components/StatCard'

interface ISeparatorDetailModalProps {
  open: boolean
  onClose: () => void
  separatorData: {
    username: string
    avg_secs: number
    total_items: number
    total_secs: number
    timeline: any[]
  } | null
}

const SeparatorDetailModal = ({
  open,
  onClose,
  separatorData
}: ISeparatorDetailModalProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { stats, timelineData } = useMemo(() => {
    if (!separatorData) {
      return {
        stats: {
          avgTime: 0,
          totalItems: 0,
          totalHours: 0,
          productivity: 0,
          timelineHours: 0
        },
        timelineData: []
      }
    }

    // Processar dados da timeline
    const timelineData = separatorData.timeline.map(entry => ({
      hour: new Date(entry.separated_hour).getHours().toString().padStart(2, '0') + ':00',
      hourNumber: new Date(entry.separated_hour).getHours(),
      avgSeconds: entry.avg_secs,
      globalAvg: entry.global_avg || 0,
      totalItems: entry.total_items,
      totalSecs: entry.total_secs
    })).sort((a, b) => a.hourNumber - b.hourNumber)

    // Calcular estatísticas
    const stats = {
      avgTime: separatorData.avg_secs,
      totalItems: separatorData.total_items,
      totalHours: Number((separatorData.total_secs / 3600).toFixed(1)),
      productivity: separatorData.total_items / (separatorData.total_secs / 3600), // itens por hora
      timelineHours: timelineData.length
    }

    return { stats, timelineData }
  }, [separatorData])

  const chartOptions: ApexOptions = {
    chart: {
      type: 'line',
      toolbar: { show: false },
      fontFamily: 'Public Sans, sans-serif',
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    markers: {
      size: 6,
      hover: {
        size: 8,
      },
    },
    xaxis: {
      categories: timelineData.map(data => data.hour),
      labels: {
        style: { fontSize: '11px' },
      },
    },
    yaxis: {
      min: 0,
      title: {
        text: 'Tempo Médio (segundos)',
        style: { fontSize: '12px', fontWeight: 600 },
      },
    },
    colors: ['#1976d2', '#FF9800'],
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4,
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val} segundos`,
      },
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const data = timelineData[dataPointIndex]
        if (!data) return ''

        const isSeparatorLine = seriesIndex === 0
        const lineLabel = isSeparatorLine ? 'Performance do Separador' : 'Média Global'
        const lineColor = isSeparatorLine ? '#1976d2' : '#FF9800'
        const value = series[seriesIndex][dataPointIndex]

        return `
          <div style="padding: 12px; min-width: 200px;">
            <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px; color: ${lineColor};">
              ${data.hour}h - ${lineLabel}
            </div>
            <div style="margin-bottom: 8px;">
              <strong>Tempo médio:</strong> ${value.toFixed(0)}s
            </div>
            ${isSeparatorLine ? `
              <div style="margin-bottom: 8px;">
                <strong>Itens processados:</strong> ${data.totalItems.toLocaleString()}
              </div>
              <div style="margin-bottom: 8px;">
                <strong>Produtividade:</strong> ${(data.totalItems / (data.totalSecs / 3600)).toFixed(1)} itens/h
              </div>
            ` : ''}
          </div>
        `
      }
    },
  }

  const series = [
    {
      name: 'Performance do Separador',
      data: timelineData.map(data => data.avgSeconds),
    },
    {
      name: 'Média Global',
      data: timelineData.map(data => data.globalAvg),
    },
  ]

  if (!separatorData) return null

  const formatSeparatorName = (username: string) => {
    return username
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isMobile ? false : "lg"}
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          minHeight: isMobile ? '100vh' : '60vh',
          maxHeight: isMobile ? '100vh' : '80vh',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
            <Person color="primary" />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Análise Detalhada - {formatSeparatorName(separatorData.username)}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Cards de Estatísticas */}
        <Grid container spacing={2} style={{ marginBottom: '24px' }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Tempo Médio"
              value={`${stats.avgTime.toFixed(0)}s`}
              color="#FFFFFF"
              gradientColors={['#1976d2', '#1565c0']}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Itens Processados"
              value={stats.totalItems.toLocaleString()}
              color="#FFFFFF"
              gradientColors={['#4CAF50', '#388e3c']}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Horas Trabalhadas"
              value={`${stats.totalHours}h`}
              color="#FFFFFF"
              gradientColors={['#FF9800', '#f57c00']}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Produtividade"
              value={`${stats.productivity.toFixed(0)} itens/h`}
              color="#FFFFFF"
              gradientColors={['#9C27B0', '#7b1fa2']}
            />
          </Grid>
        </Grid>

        <Divider style={{ margin: '12px 0' }} />

        {/* Gráfico de Evolução Temporal */}
        <Box>
          <Typography variant="h6" style={{ marginBottom: '16px', fontWeight: 600 }}>
            Evolução Temporal da Performance
          </Typography>
          <Box style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Box style={{ width: '16px', height: '3px', backgroundColor: '#1976d2', borderRadius: '2px' }}></Box>
              <Typography variant="body2" style={{ color: '#666' }}>
                Performance do Separador
              </Typography>
            </Box>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Box style={{ width: '16px', height: '3px', backgroundColor: '#FF9800', borderRadius: '2px' }}></Box>
              <Typography variant="body2" style={{ color: '#666' }}>
                Média Global
              </Typography>
            </Box>
          </Box>
          <Box 
            style={{ 
              paddingBottom: timelineData.length > 8 ? '40px' : '20px',
              overflowX: 'auto',
              overflowY: 'hidden'
            }}
          >
            <Box style={{ minWidth: isMobile ? '600px' : 'auto' }}>
              <ReactApexChart
                options={chartOptions}
                series={series}
                type="line"
                height={320}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default SeparatorDetailModal
